/**
 * The ONE place that decides whether an awardee belongs to an academy.
 *
 * Two endpoints serve these columns in two different shapes, and both are correct:
 *
 *   /awardeeByAwardIdawardName  raw `awardees` rows  -> marker CHARACTERS ('m', '*', ...)
 *   /recent-awardees, /awardees `awardee_summary`    -> 0/1 INTEGERS
 *
 * So the test has to be shape-aware. Never compare against a specific marker
 * character. NAM was changed from '#' to 'm' upstream; the backend was fixed to
 * test "carries any marker" (blueprints/awardees.py:72-76,
 * scripts/refresh_summary.py:51-55) but the frontend copy was missed and the
 * National Academy of Medicine badge silently vanished from every award roster
 * for 6,595 memberships across 1,333 award pages. No error, no empty state.
 *
 * That happened because the same decode was reimplemented in four files under
 * three incompatible rule sets. Import from here instead of writing a fifth.
 */

/**
 * True when the value carries an academy marker, whatever shape it arrived in.
 *
 * Mirrors the backend predicate byte for byte:
 *   TRIM(REPLACE(REPLACE(col, CHAR(13), ''), CHAR(10), '')) <> ''
 *
 * CR/LF is stripped before trimming because these cells are known to pack stray
 * line endings (one NAE row is a lone '\r', and 12 AAS rows are ' a' with a
 * leading space - an exact === 'a' match drops those).
 */
export const hasAcademyMarker = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const marker = String(value).replace(/[\r\n]/g, "").trim();
  // The "0" guard matters: a summary-backed row serialized as a string would
  // otherwise read a literal "0" as membership.
  return marker !== "" && marker !== "0";
};

/** Field names as they arrive from the API, in the order badges should render. */
export const ACADEMY_KEYS = ["NAS", "NAE", "NAM", "NAI", "AAS"];

/**
 * Decode one API row into lowercase boolean flags: { nam, nae, nas, nai, aas }.
 * Lowercase because that is what the grid renderers already key on.
 */
export const academyFlags = (row) =>
  ACADEMY_KEYS.reduce((flags, key) => {
    flags[key.toLowerCase()] = hasAcademyMarker(row?.[key]);
    return flags;
  }, {});

/**
 * How many academies this row belongs to.
 *
 * Prefer the backend's `academyCount` when the endpoint supplies one; this is
 * for raw-row surfaces that have to compute it. Derived from the same flags the
 * badges use, so the number can never disagree with the icons next to it.
 */
export const academyCount = (row) =>
  Object.values(academyFlags(row)).filter(Boolean).length;
